import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { PlatformService } from "../platform/platform.service";
import { GlobalConstants } from "@src/app/global-constants";
import { Observable } from "rxjs";
import { Comentario } from "@src/app/models/interfaces/comentario.interface";

@Injectable()
export class ComentariosService {
  private readonly API_URL: any;
  private readonly API_SHARED_URL: any;
  private readonly constantesGlobales: any;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly platformService: PlatformService,
  ) {
    const host = this.platformService.getLocationHostname();
    this.constantesGlobales = new GlobalConstants(host);
    this.API_URL = this.constantesGlobales.urlApi;
    this.API_SHARED_URL = this.constantesGlobales.urlSharedApi;
  }

  /**
   * Obtiene los comentarios de un video específico, con la opción de filtrar por un ID de usuario.
   *
   * @param idVideo - El identificador único del video para el cual se desean obtener los comentarios.
   * @param userId - (Opcional) El identificador del usuario para filtrar los comentarios relacionados con dicho usuario.
   * @returns Un observable que emite la respuesta con los comentarios obtenidos.
   */
  public getComentarios(idVideo: string, userId?: number): Observable<Comentario[]> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    let body = new HttpParams();
    if (userId) {
      body = body.set("userId", String(userId));
    }
    return this.httpClient.get<Comentario[]>(`${this.API_SHARED_URL}/comments/allByDate/${idVideo}`, { headers: headers, params: body });
  }
  /**
   * Reporta un comentario específico en el sistema.
   *
   * @param idComment - El identificador único del comentario que se desea reportar.
   * @param idUser - El identificador único del usuario que realiza el reporte.
   * @returns Un observable que emite la respuesta del servidor después de realizar la solicitud de reporte.
   *
   * La solicitud se realiza con el encabezado `Content-Type` configurado como
   * `application/x-www-form-urlencoded` y acepta respuestas en formato JSON.
   */
  public reportComment(idComment: string, idUser: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = {
      userId: String(idUser),
    };

    return this.httpClient.post<any>(`${this.API_SHARED_URL}/comments/report/${idComment}`, body, { headers: headers });
  }
  /**
   * Envía una solicitud para calificar un comentario.
   *
   * @param idComment - El identificador único del comentario que se desea calificar.
   * @param idUser - El identificador único del usuario que realiza la calificación. Este parámetro es opcional.
   * @param type - El tipo de calificación que se desea aplicar (por ejemplo, "like" o "dislike").
   * @returns Un observable que emite la respuesta de la solicitud HTTP.
   */
  public rateComment(idComment: string, idUser: number, type: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = {
      idAmbito: this.constantesGlobales.idAmbito,
      idComment: String(idComment),
      type: type,
    };

    if (idUser) {
      body["userId"] = idUser;
    }
    return this.httpClient.post<any>(`${this.API_SHARED_URL}/comments/rate`, body, { headers: headers });
  }

  /**
   * Envía un comentario asociado a un video.
   *
   * @param userId - Identificador del usuario que envía el comentario.
   * @param idVideo - Identificador del video al que pertenece el comentario.
   * @param mensaje - Contenido del comentario.
   * @param idPadre - (Opcional) Identificador del comentario padre, en caso de ser una respuesta.
   * @returns Un observable que emite la respuesta de la solicitud HTTP.
   */
  public sendComentario(userId: any, idVideo: number, mensaje: string, idPadre?: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = {
      idVideo,
      userId,
      mensaje,
    };
    if (idPadre) {
      body["idPadre"] = idPadre;
    }
    return this.httpClient.post<any>(`${this.API_SHARED_URL}/comments/create`, body, { headers: headers, observe: "response" });
  }

  /**
   * Envía un "like" a un video específico asociado a un usuario.
   *
   * @param userId - El identificador del usuario que envía el "like".
   * @param idVideo - El identificador del video al que se le da el "like".
   * @returns Un observable que emite la respuesta del servidor.
   */
  public sendLike(userId: any, idVideo: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = new HttpParams().set("userId", userId).set("idVideo", idVideo);
    return this.httpClient.get<any>(this.API_URL + "/sendLikeNoticia", { headers: headers, params: body });
  }

  /**
   * Obtiene los likes y comentarios de un video específico.
   *
   * @param idVideo - El identificador único del video.
   * @param idUsuario - (Opcional) El identificador único del usuario.
   * @returns Un observable que emite los datos de likes y comentarios del video.
   */
  public getLikesComents(idVideo: string, idUsuario?: any): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    let body = new HttpParams().set("id_video", idVideo);
    if (idUsuario) body = body.set("id_usuario", String(idUsuario));
    return this.httpClient.get<any>(this.API_URL + "/noticiasLikesComentarios", { headers: headers, params: body });
  }

  /**
   * Obtiene los comentarios de una noticia en concreto aportando su id.
   * @param {number} idVideo - Id de la noticia.
   * @returns {Observable<any>} - Observable con la respuesta de la petición.
   */
  public getComentariosPrensa(idVideo): Observable<Comentario[]> {
    if (isNaN(idVideo)) return;
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = new HttpParams().set("id_video", String(idVideo));
    return this.httpClient.get<Comentario[]>(this.API_SHARED_URL + "/getComentariosNoticia", { headers: headers, params: body });
  }
}
